import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import SearchTextBox from 'srcReact/components/common2/SearchTextBox'
import Tabs from 'srcReact/components/common2/Tabs'
import styles from './styles.module.scss'

import MixrList from './MixrList'
import DraftList from './DraftList'
import { getCurrentOrgId } from 'srcReact/helpers/organizationHelper'
import { urlSearchParams } from 'srcReact/helpers/utils'
import { mixrActions } from 'srcReact/Mixr/actions'
import { commonActions } from 'srcReact/components'
import Modal from 'srcReact/components/common2/Modal'
import Layout from 'srcReact/Mixr/MixrHome/components/Layout'
import Button from 'srcReact/components/common2/Button'
import Dropdown1 from 'srcReact/components/common2/Dropdown1'
import { decode } from 'html-entities'
import MixrGroupListItem from 'srcReact/components/common2/MixrGroupListItem'

function Container({ setHasFormErrors, hideModal, attachedGroupId }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    common: { isNative, isMobile, groupsByOrgId },
    login: { user: loggedUser },
    mixr: { mixr }
  } = useSelector((state) => state)
  const [currentOrgId, setCurrentOrgId] = useState(0)
  const [activeTab, setActiveTab] = useState(0)
  const [previewTriggered, setPreviewTriggered] = useState(false)
  const [scope, setScope] = useState('draft')
  const [search, setSearch] = useState('')
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [groups, setGroups] = useState([])
  const [selectedGroup, setSelectedGroup] = useState({ value: 0, label: t('event.create.startFrom.allGroups') })

  useEffect(() => {
    setScope('draft')
    if (isNative) {
      setCurrentOrgId(urlSearchParams('organization_id'))
    } else {
      setCurrentOrgId(getCurrentOrgId())
    }
  }, [])

  useEffect(() => {
    if (groupsByOrgId?.list?.length > 0 && groups?.length === 0) {
      const groupsByOrg = groupsByOrgId.list
      const groupList = []
      groupList.push({ value: 0, label: t('event.create.startFrom.allGroups') })
      for (let i = 0; i < groupsByOrg.length; i++) {
        const item = groupsByOrg[i]
        const objGroup = {
          group: item,
          value: groupsByOrg[i].group_id,
          label: (
            <MixrGroupListItem
              className={styles.groupItem}
              id={item.group_id}
              picture={item.picture.url}
              title={decode(item.group_name)}
              type={'group'}
              titleCSS={styles.groupMixrTitle}
              groupRole={item.group_role}
              disableRedirection={true}
            />
          )
        }
        if (objGroup.value == attachedGroupId) {
          setSelectedGroup(objGroup)
        }
        groupList.push(objGroup)
      }
      setGroups([...groupList])
    }
  }, [groupsByOrgId])

  useEffect(() => {
    if (mixr && previewTriggered) {
      setPreviewTriggered(false)
      setShowPreviewModal(true)
    }
  }, [mixr])

  const showDrafts = () => {
    setScope('draft')
  }

  const showMixrs = () => {
    setScope('mixr')
  }

  const handleGroupChange = (group) => {
    if (group.value === 0) {
      setSelectedGroup(group)
    } else {
      const objGroup = {
        value: group.value,
        label: (
          <MixrGroupListItem
            className={styles.groupItem}
            id={group.value}
            picture={group.group.picture.url}
            title={decode(group.group.group_name)}
            type={'group'}
            titleCSS={styles.groupMixrTitle}
            groupRole={group.group.group_role}
            disableRedirection={true}
          />
        )
      }
      setSelectedGroup(objGroup)
    }
    getMixrsOrDrafts(search, group.value)
  }

  const hidePreviewModal = () => setShowPreviewModal(false)

  const handleUseDraftFromModal = () => {
    dispatch(mixrActions.setPreviewFromModalFlag(false))
    setShowPreviewModal(false)
    hideModal()
  }

  const handlePreviewMixrOrDraft = (id) => {
    const attachedGroup = groupsByOrgId?.list?.find((x) => x.group_id == attachedGroupId)
    setPreviewTriggered(true)
    if (scope === 'mixr') {
      dispatch(mixrActions.getMixrDetails(id, '', true, loggedUser, true, attachedGroup))
    } else if (scope === 'draft') {
      dispatch(mixrActions.getDraftDetail(id, true, loggedUser, true, attachedGroup))
    }
  }

  const handleUseMixr = (id) => {
    const attachedGroup = groupsByOrgId?.list?.find((x) => x.group_id == attachedGroupId)
    dispatch(commonActions.fileCreationRemove())
    dispatch(commonActions.updateSelectedInterests([]))
    dispatch(mixrActions.resetSaveDraftData())
    setHasFormErrors(false)
    if (scope === 'mixr') {
      dispatch(mixrActions.getMixrDetails(id, '', true, loggedUser, false, attachedGroup))
    } else if (scope === 'draft') {
      dispatch(mixrActions.getDraftDetail(id, true, loggedUser, false, attachedGroup))
    }
    hideModal()
  }

  const handleSearch = (searchValue) => {
    setSearch(searchValue)
    getMixrsOrDrafts(searchValue, selectedGroup.value)
  }

  const getMixrsOrDrafts = (search, searchGroupId) => {
    if (scope === 'draft') {
      dispatch(
        mixrActions.getDrafts(currentOrgId, mixr?.group?.group_id, searchGroupId, search, {
          nextOffset: 0,
          isPagination: false
        })
      )
    } else if (scope === 'mixr') {
      dispatch(
        mixrActions.getExistingMixrList(currentOrgId, mixr?.group?.group_id, searchGroupId, search, {
          nextOffset: 0,
          isPagination: false
        })
      )
    }
  }

  return (
    <React.Fragment>
      <div className={styles.modalContainer}>
        <div className={styles.filters}>
          <SearchTextBox
            cssClass={styles.search}
            inputCssClass={styles.searchInput}
            handleSearch={handleSearch}
            placeholder={t('event.create.startFrom.placeholder')}
            style='secondary'
          />
          <Dropdown1
            className={styles.groupDropdown}
            value={selectedGroup}
            options={groups}
            onChange={handleGroupChange}
          />
        </div>
        <Tabs type='boxed' contentClassName={styles.tabContent} initialActive={activeTab}>
          <div onClick={showDrafts} label={`${t('event.create.startFrom.draftsTab')}`}>
            <DraftList
              search={search}
              currentOrgId={currentOrgId}
              groupId={mixr?.group?.group_id}
              searchGroupId={selectedGroup.value}
              handleUseMixrOrDraft={handleUseMixr}
              handlePreviewMixrOrDraft={handlePreviewMixrOrDraft}
            />
          </div>
          <div onClick={showMixrs} label={`${t('global.mixrs')} `}>
            <MixrList
              search={search}
              currentOrgId={currentOrgId}
              groupId={mixr?.group?.group_id}
              searchGroupId={selectedGroup.value}
              handleUseMixrOrDraft={handleUseMixr}
              handlePreviewMixrOrDraft={handlePreviewMixrOrDraft}
            />
          </div>
        </Tabs>
      </div>
      {showPreviewModal && (
        <Modal
          showDefaultHeader={false}
          className='mixr-layout'
          handleClose={hidePreviewModal}
          showClose={true}
          closeOnDocumentClick={true}
          nested
          isOpen={showPreviewModal}>
          <div className={styles.mixrLayoutModalContainer}>
            <div className={styles.headerButtons}>
              <Button
                style='secondary'
                customStyle={styles.btnUseDraft}
                text={t('global.cancel')}
                handleClick={hidePreviewModal}
              />
              <Button
                customStyle={styles.btnUseDraft}
                text={scope === 'draft' ? t('event.create.startFrom.useDraft') : t('event.create.startFrom.useMixr')}
                handleClick={handleUseDraftFromModal}
              />
            </div>
            <Layout
              mixrId={''}
              isMixrNotOver={true}
              isMixrParticipant={true}
              isGoing={true}
              isGroupMember={true}
              isStaffMember={true}
              isOrganizer={true}
              isPrivateGroup={false}
              isPendingApproval={false}
              isSuggestedMixr={false}
              totalActiveUsersCount={1}
              activeTab={0}
              setShowJoinGroupPopup={() => {}}
              setShowCancelMixrPopup={() => {}}
              setActiveTab={() => {}}
              setShowAddToCalendarPopup={() => {}}
              setFeedIdToScroll={() => {}}
              selectParticipantsTab={() => {}}
              getThreeDotsOptions={() => {}}
              handleAddCoorganizer={() => {}}
              handleRemoveCoorganizer={() => {}}
              handleRSVPChange={() => {}}
              handleAddToCalendar={() => {}}
            />
          </div>
        </Modal>
      )}
    </React.Fragment>
  )
}

export default Container
